import { z } from "zod";
import { CREATOR_FOLLOWER_RANGES, CREATOR_PLATFORMS } from "../creators";
import { SUPPORTED_LANGS } from "../i18n/langs";
import { AMBASSADOR_HANDLE_RE, type AmbassadorKind, type EarningState } from "./earnings";

export * from "./earnings";
export * from "./links";
export * from "./badge-art";

/** Moroccan RIB: 24 digits. Spaces are stripped before checking. */
export const ribSchema = z
  .string()
  .transform((v) => v.replace(/\s+/g, ""))
  .pipe(z.string().regex(/^\d{24}$/));

/** Web links only: a `javascript:` or `data:` URL would run on the public page. */
const url = z.string().trim().max(500).pipe(z.url({ protocol: /^https?$/ }));

/** One platform the applicant posts on, with the link to their profile there. */
export const ambassadorPlatformLinkSchema = z.object({
  platform: z.enum(CREATOR_PLATFORMS),
  url,
});
export type AmbassadorPlatformLink = z.infer<typeof ambassadorPlatformLinkSchema>;

export const ambassadorPlatformsSchema = z
  .array(ambassadorPlatformLinkSchema)
  .min(1)
  .max(CREATOR_PLATFORMS.length)
  .refine((list) => new Set(list.map((p) => p.platform)).size === list.length, {
    message: "Each platform once",
  });

/**
 * Before 2026-10-09 the form sent one `platform` + `profileUrl`. A cached page
 * or an app build from before then still does, so that shape becomes a
 * one-entry `platforms` list. When both are sent, `platforms` wins.
 */
function liftSinglePlatform(raw: unknown): unknown {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return raw;
  const r = raw as Record<string, unknown>;
  if (r.platforms !== undefined || r.platform === undefined) return raw;
  return { ...r, platforms: [{ platform: r.platform, url: r.profileUrl }] };
}

export const ambassadorApplicationInputSchema = z.preprocess(
  liftSinglePlatform,
  z.object({
    fullName: z.string().trim().min(2).max(120),
    email: z.string().trim().email().max(200),
    platforms: ambassadorPlatformsSchema,
    followers: z.enum(CREATOR_FOLLOWER_RANGES),
    about: z.string().trim().min(10).max(2000),
    lang: z.enum(SUPPORTED_LANGS).optional(),
  }),
);
export type AmbassadorApplicationInput = z.infer<typeof ambassadorApplicationInputSchema>;

export const ambassadorProfilePatchSchema = z.object({
  handle: z
    .string()
    .transform((v) => v.trim().replace(/^@/, "").toLowerCase())
    .pipe(z.string().regex(AMBASSADOR_HANDLE_RE))
    .optional(),
  displayName: z.string().trim().min(2).max(80).optional(),
  bio: z.string().trim().max(280).optional(),
  avatarUrl: url.nullable().optional(),
  coverUrl: url.nullable().optional(),
  instagram: url.nullable().optional(),
  tiktok: url.nullable().optional(),
  youtube: url.nullable().optional(),
  facebook: url.nullable().optional(),
  categoryIds: z
    .array(z.string().uuid())
    .min(1)
    .max(20)
    .transform((ids) => [...new Set(ids)])
    .optional(),
  ribHolder: z.string().trim().min(2).max(120).nullable().optional(),
  ribBank: z.string().trim().min(2).max(80).nullable().optional(),
  rib: ribSchema.nullable().optional(),
  /** Sent once by the last onboarding step. */
  finishOnboarding: z.boolean().optional(),
});
export type AmbassadorProfilePatch = z.infer<typeof ambassadorProfilePatchSchema>;

/** How many favourites an ambassador can pin to the top of their page. */
export const AMBASSADOR_MAX_PINS = 3;

/**
 * PUT /ambassadors/me/favourites/pins: the pinned favourites, first one on
 * top. The whole list each time; an empty list unpins everything.
 */
export const ambassadorPinsSchema = z.object({
  activityIds: z
    .array(z.string().uuid())
    .max(AMBASSADOR_MAX_PINS)
    .transform((ids) => [...new Set(ids)]),
});
export type AmbassadorPinsInput = z.infer<typeof ambassadorPinsSchema>;

export const AMBASSADOR_STATUSES = ["onboarding", "active", "paused"] as const;
export type AmbassadorStatus = (typeof AMBASSADOR_STATUSES)[number];
export const AMBASSADOR_APPLICATION_STATUSES = ["pending", "approved", "rejected"] as const;
export type AmbassadorApplicationStatus = (typeof AMBASSADOR_APPLICATION_STATUSES)[number];

export type AmbassadorCard = {
  id: string;
  slug: string;
  title: string;
  href: string;
  heroImageUrl: string | null;
  priceFromMad: number | null;
  template: string | null;
  categorySlug: string | null;
  citySlug: string | null;
};

/** GET /ambassadors/profile/:handle */
export type AmbassadorPublicProfile = {
  handle: string;
  displayName: string;
  bio: string;
  avatarUrl: string | null;
  coverUrl: string | null;
  socials: { instagram: string | null; tiktok: string | null; youtube: string | null; facebook: string | null };
  categories: { id: string; slug: string; name: string }[];
  favourites: (AmbassadorCard & { categoryId: string | null })[];
};

/** GET /ambassadors/me */
export type AmbassadorMe = {
  application: { id: string; status: AmbassadorApplicationStatus; createdAt: string } | null;
  ambassador:
    | (AmbassadorPublicProfile & {
        id: string;
        status: AmbassadorStatus;
        hasRib: boolean;
        ribLast4: string | null;
        favouriteIds: string[];
        /** Pinned favourites, top one first (at most AMBASSADOR_MAX_PINS). */
        pinnedIds: string[];
      })
    | null;
};

/** GET /ambassadors/me/summary. All amounts are whole MAD. */
export type AmbassadorSummary = {
  nextPayoutDate: string;
  waitingMad: number;
  waitingCount: number;
  dueMad: number;
  dueCount: number;
  earnedMad: number;
  clicks: number;
  bookings: number;
};

/** GET /ambassadors/me/bookings. No guest names, ever. */
export type AmbassadorBookingRow = {
  id: string;
  activityTitle: string;
  activityDate: string | null;
  kind: AmbassadorKind;
  shareMad: number;
  state: EarningState;
  payoutDate: string | null;
  /**
   * The booking itself was cancelled. A `void` row is either this or a
   * commission of zero (a no-show, a correction); the dashboard words them apart.
   */
  cancelled: boolean;
};

/** GET /ambassadors/me/links?q= */
export type AmbassadorLinkRow = AmbassadorCard & {
  kind: AmbassadorKind | null;
  /** Whole MAD per guest; set only when earnPer is "guest". */
  earnMad: number | null;
  /**
   * "guest": earnMad per guest (an even per-head rate, so it halves exactly).
   * "half": half our commission (percent rates, or odd per-head rates whose
   * share rounds on the booking total). "none": no commission on it yet.
   */
  earnPer: "guest" | "half" | "none" | null;
};

/**
 * GET /ambassadors/me/links/stats: each listing link's clicks per day over
 * the last `days.length` days (Morocco dates, oldest first) and the bookings
 * it brought in that window. Only listings with a click or a booking.
 * Clicks per link are counted from October 2026; visits to the ambassador's
 * own page count in the summary total, never here.
 */
export type AmbassadorLinkStats = {
  days: string[];
  links: {
    activityId: string;
    title: string;
    href: string;
    /** One count per entry of `days`. */
    clicks: number[];
    totalClicks: number;
    bookings: number;
  }[];
};
